// ===========================================
// Import Progress Modal
// ===========================================
// Simulated progress modal for CSV import execution.
// Cycles through known import steps at weighted intervals
// while the real backend request is in flight. On response,
// jumps to 100% (success) or stops with error.

import { useState, useEffect, useRef, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  LinearProgress,
  Alert,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  CircularProgress,
} from '@mui/material';
import {
  CheckCircle,
  ErrorOutline,
  RadioButtonUnchecked,
} from '@mui/icons-material';

// ===========================================
// TYPES
// ===========================================

export type ModalPhase = 'importing' | 'success' | 'error';

interface ImportProgressModalProps {
  open: boolean;
  phase: ModalPhase;
  errorMessage?: string;
  onClose: () => void;
}

// ===========================================
// STEP DEFINITIONS
// ===========================================

interface ImportStep {
  key: string;
  label: string;
  weight: number;
}

const IMPORT_STEPS: ImportStep[] = [
  { key: 'locations', label: 'Creating locations', weight: 1 },
  { key: 'rooms', label: 'Creating rooms', weight: 1 },
  { key: 'instruments', label: 'Creating instruments', weight: 1 },
  { key: 'programLevels', label: 'Creating program levels', weight: 1 },
  { key: 'lessonDurations', label: 'Creating lesson durations', weight: 1 },
  { key: 'lessonTypes', label: 'Creating lesson types', weight: 1 },
  { key: 'teachers', label: 'Creating teacher accounts', weight: 3 },
  { key: 'families', label: 'Creating families', weight: 2 },
  { key: 'parents', label: 'Creating parent accounts', weight: 3 },
  { key: 'students', label: 'Creating student records', weight: 4 },
  { key: 'lessons', label: 'Creating lessons', weight: 4 },
  { key: 'enrollments', label: 'Matching student enrollments', weight: 5 },
];

const TOTAL_WEIGHT = IMPORT_STEPS.reduce((sum, s) => sum + s.weight, 0); // 27
const ESTIMATED_DURATION_MS = 15_000;
const TICK_INTERVAL_MS = 100;
const MAX_SIMULATED_PROGRESS = 95;

// ===========================================
// COMPONENT
// ===========================================

export default function ImportProgressModal({
  open,
  phase,
  errorMessage,
  onClose,
}: ImportProgressModalProps) {
  const [progress, setProgress] = useState(0);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const elapsedRef = useRef(0);

  // Build cumulative time thresholds per step
  const stepThresholds = useRef(
    (() => {
      const thresholds: number[] = [];
      let cumulative = 0;
      for (const step of IMPORT_STEPS) {
        cumulative += (step.weight / TOTAL_WEIGHT) * ESTIMATED_DURATION_MS;
        thresholds.push(cumulative);
      }
      return thresholds;
    })()
  ).current;

  // Reset state whenever modal opens for a new import
  useEffect(() => {
    if (open && phase === 'importing') {
      setProgress(0);
      setCurrentStepIndex(0);
      elapsedRef.current = 0;
    }
  }, [open, phase]);

  // Animate progress during 'importing' phase
  useEffect(() => {
    if (!open || phase !== 'importing') {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
      return;
    }

    timerRef.current = setInterval(() => {
      elapsedRef.current += TICK_INTERVAL_MS;
      const elapsed = elapsedRef.current;

      // Calculate progress as percentage, capped at MAX_SIMULATED_PROGRESS
      const rawProgress = (elapsed / ESTIMATED_DURATION_MS) * MAX_SIMULATED_PROGRESS;
      const clampedProgress = Math.min(rawProgress, MAX_SIMULATED_PROGRESS);
      setProgress(clampedProgress);

      // Determine which step we're on based on elapsed time
      let stepIdx = 0;
      for (let i = 0; i < stepThresholds.length; i++) {
        if (elapsed >= stepThresholds[i]) {
          stepIdx = i + 1;
        }
      }
      setCurrentStepIndex(Math.min(stepIdx, IMPORT_STEPS.length - 1));
    }, TICK_INTERVAL_MS);

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [open, phase, stepThresholds]);

  // Jump to 100% on success
  useEffect(() => {
    if (phase === 'success') {
      setProgress(100);
      setCurrentStepIndex(IMPORT_STEPS.length - 1);
    }
  }, [phase]);

  const handleDialogClose = useCallback(
    (_event: object, reason: 'backdropClick' | 'escapeKeyDown') => {
      // Block all dismissal during import (backdrop click + escape key)
      if (phase === 'importing') return;
      // Allow close via backdrop/escape only when finished
      if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
        onClose();
      }
    },
    [phase, onClose]
  );

  const displayProgress = Math.round(progress);

  return (
    <Dialog
      open={open}
      onClose={handleDialogClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        {phase === 'success'
          ? 'Import Complete'
          : phase === 'error'
            ? 'Import Failed'
            : 'Importing Data...'}
      </DialogTitle>

      <DialogContent dividers>
        {/* Progress bar with percentage */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Box sx={{ flex: 1, mr: 2 }}>
            <LinearProgress
              variant="determinate"
              value={displayProgress}
              color={phase === 'error' ? 'error' : 'primary'}
              sx={{ height: 8, borderRadius: 4 }}
            />
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ minWidth: 40 }}>
            {displayProgress}%
          </Typography>
        </Box>

        {/* Current step label */}
        {phase === 'importing' && (
          <Typography variant="subtitle2" sx={{ mb: 2 }}>
            {IMPORT_STEPS[currentStepIndex].label}...
          </Typography>
        )}

        {/* Success alert */}
        {phase === 'success' && (
          <Alert severity="success" sx={{ mb: 2 }}>
            All data imported successfully. You can close this dialog to view the results.
          </Alert>
        )}

        {/* Error alert */}
        {phase === 'error' && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {errorMessage || 'An unexpected error occurred during import.'}
          </Alert>
        )}

        {/* Step checklist */}
        <List dense disablePadding>
          {IMPORT_STEPS.map((step, idx) => {
            const isCompleted =
              phase === 'success' || idx < currentStepIndex;
            const isCurrent =
              phase === 'importing' && idx === currentStepIndex;
            const isErrorStep =
              phase === 'error' && idx === currentStepIndex;

            let icon;
            if (isCompleted) {
              icon = <CheckCircle color="success" fontSize="small" />;
            } else if (isCurrent) {
              icon = <CircularProgress size={18} />;
            } else if (isErrorStep) {
              icon = <ErrorOutline color="error" fontSize="small" />;
            } else {
              icon = (
                <RadioButtonUnchecked
                  fontSize="small"
                  sx={{ color: 'text.disabled' }}
                />
              );
            }

            return (
              <ListItem key={step.key} disableGutters sx={{ py: 0.25 }}>
                <ListItemIcon sx={{ minWidth: 32 }}>
                  {icon}
                </ListItemIcon>
                <ListItemText
                  primary={step.label}
                  primaryTypographyProps={{
                    variant: 'body2',
                    fontWeight: isCurrent || isErrorStep ? 600 : 400,
                    color: isCompleted || isCurrent || isErrorStep
                      ? 'text.primary'
                      : 'text.disabled',
                  }}
                />
              </ListItem>
            );
          })}
        </List>
      </DialogContent>

      {/* Actions -- only show Close when not importing */}
      {phase !== 'importing' && (
        <DialogActions>
          <Button onClick={onClose} variant="contained">
            Close
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
}
